import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
  arrayUnion,
  query,
  where,
  getDocs
} from 'firebase/firestore';

class IPService {
  // Detect if IP is IPv6
  isIPv6(ip) {
    return ip.includes(':');
  }

  // Get IPv6 network prefix (/64 for residential networks)
  getIPv6NetworkPrefix(ipv6) {
    if (!this.isIPv6(ipv6)) return ipv6;
    
    // Split IPv6 into segments
    const segments = ipv6.split(':');
    
    // Take first 4 segments (64 bits) for network prefix
    // This covers most residential IPv6 allocations
    const networkPrefix = segments.slice(0, 4).join(':') + '::/64';
    
    return networkPrefix;
  }

  // Get the effective IP/network for limitation purposes
  getEffectiveIP(ip) {
    if (this.isIPv6(ip)) {
      return this.getIPv6NetworkPrefix(ip);
    }
    return ip; // IPv4 stays as-is
  }

  // Get current user's IP address from Netlify function
  async getCurrentIP() {
    try {
      const response = await fetch('/.netlify/functions/get-user-ip');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error getting IP:', error);
      return 'unknown';
    }
  }

  // Check if IP/network is banned
  async checkIPBanned(ip) {
    try {
      const effectiveIP = this.getEffectiveIP(ip);
      
      // Check the effective IP (network prefix for IPv6)
      const ipDoc = await getDoc(doc(db, 'ip_addresses', effectiveIP));
      if (ipDoc.exists()) {
        const data = ipDoc.data();
        if (data.banned === true) return true;
      }

      // For IPv6, also check if any individual IPs in this network are banned
      if (this.isIPv6(ip)) {
        const individualDoc = await getDoc(doc(db, 'ip_addresses', ip));
        if (individualDoc.exists()) {
          const data = individualDoc.data();
          if (data.banned === true) return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking IP ban:', error);
      return false;
    }
  }

  // Check if IP/network is limited (cannot create new accounts)
  async checkIPLimited(ip) {
    try {
      const effectiveIP = this.getEffectiveIP(ip);
      
      // Check the effective IP (network prefix for IPv6)
      const ipDoc = await getDoc(doc(db, 'ip_addresses', effectiveIP));
      if (ipDoc.exists()) {
        const data = ipDoc.data();
        if (data.limited === true) return true;
      }

      // For IPv6, also check if any individual IPs in this network are limited
      if (this.isIPv6(ip)) {
        const individualDoc = await getDoc(doc(db, 'ip_addresses', ip));
        if (individualDoc.exists()) {
          const data = individualDoc.data();
          if (data.limited === true) return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking IP limit:', error);
      return false;
    }
  }

  // Get all users from a network (for IPv6 networks)
  async getUsersFromNetwork(networkPrefix) {
    try {
      const ipQuery = query(
        collection(db, 'ip_addresses'),
        where('networkPrefix', '==', networkPrefix)
      );
      
      const snapshot = await getDocs(ipQuery);
      const allUsers = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.users) {
          allUsers.push(...data.users);
        }
      });

      // Remove duplicates based on userId
      const uniqueUsers = allUsers.filter((user, index, self) => 
        index === self.findIndex(u => u.userId === user.userId)
      );

      return uniqueUsers;
    } catch (error) {
      console.error('Error getting users from network:', error);
      return [];
    }
  }

  // Log IP for a user with network grouping
  async logUserIP(userId, userEmail, ip) {
    try {
      const effectiveIP = this.getEffectiveIP(ip);
      const isIPv6 = this.isIPv6(ip);

      // Always log the individual IP
      await this.logIndividualIP(userId, userEmail, ip, effectiveIP);

      // For IPv6, also manage the network prefix document
      if (isIPv6 && effectiveIP !== ip) {
        await this.logNetworkPrefix(userId, userEmail, ip, effectiveIP);
      }

      // Update user document with IP
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        await updateDoc(userRef, {
          ips: arrayUnion(ip),
          lastIP: ip,
          lastActivity: serverTimestamp(),
          ...(isIPv6 && { ipv6Network: effectiveIP })
        });
      } else {
        await setDoc(userRef, {
          userId,
          email: userEmail,
          ips: [ip],
          lastIP: ip,
          createdAt: serverTimestamp(),
          lastActivity: serverTimestamp(),
          ...(isIPv6 && { ipv6Network: effectiveIP })
        });
      }

      return true;
    } catch (error) {
      console.error('Error logging user IP:', error);
      return false;
    }
  }

  // Log individual IP address
  async logIndividualIP(userId, userEmail, ip, effectiveIP) {
    const ipRef = doc(db, 'ip_addresses', ip);
    const ipDoc = await getDoc(ipRef);

    if (ipDoc.exists()) {
      const existingData = ipDoc.data();
      const existingUsers = existingData.users || [];
      
      const userIndex = existingUsers.findIndex(u => u.userId === userId);
      
      if (userIndex >= 0) {
        existingUsers[userIndex] = {
          userId,
          email: userEmail,
          lastSeen: new Date()
        };
      } else {
        existingUsers.push({
          userId,
          email: userEmail,
          lastSeen: new Date()
        });
      }
      
      await updateDoc(ipRef, {
        users: existingUsers,
        lastActivity: serverTimestamp(),
        ...(this.isIPv6(ip) && { networkPrefix: effectiveIP })
      });
    } else {
      await setDoc(ipRef, {
        ip,
        users: [{
          userId,
          email: userEmail,
          lastSeen: new Date()
        }],
        firstSeen: serverTimestamp(),
        lastActivity: serverTimestamp(),
        banned: false,
        limited: false,
        type: this.isIPv6(ip) ? 'ipv6' : 'ipv4',
        ...(this.isIPv6(ip) && { networkPrefix: effectiveIP })
      });
    }
  }

  // Log network prefix (for IPv6 networks)
  async logNetworkPrefix(userId, userEmail, individualIP, networkPrefix) {
    const networkRef = doc(db, 'ip_addresses', networkPrefix);
    const networkDoc = await getDoc(networkRef);

    // Get all users from this network
    const networkUsers = await this.getUsersFromNetwork(networkPrefix);
    
    // Add current user if not already in network
    const userIndex = networkUsers.findIndex(u => u.userId === userId);
    if (userIndex >= 0) {
      networkUsers[userIndex] = {
        userId,
        email: userEmail,
        lastSeen: new Date(),
        lastIP: individualIP
      };
    } else {
      networkUsers.push({
        userId,
        email: userEmail,
        lastSeen: new Date(),
        lastIP: individualIP
      });
    }

    if (networkDoc.exists()) {
      await updateDoc(networkRef, {
        users: networkUsers,
        lastActivity: serverTimestamp(),
        lastSeenIP: individualIP
      });
    } else {
      await setDoc(networkRef, {
        ip: networkPrefix,
        users: networkUsers,
        firstSeen: serverTimestamp(),
        lastActivity: serverTimestamp(),
        banned: false,
        limited: false,
        type: 'ipv6_network',
        lastSeenIP: individualIP,
        isNetworkPrefix: true
      });
    }
  }

  // Log IP for anonymous visitor
  async logAnonymousIP(ip) {
    try {
      const effectiveIP = this.getEffectiveIP(ip);
      
      // Log individual IP
      const ipRef = doc(db, 'ip_addresses', ip);
      const ipDoc = await getDoc(ipRef);

      if (ipDoc.exists()) {
        await updateDoc(ipRef, {
          anonymousVisits: (ipDoc.data().anonymousVisits || 0) + 1,
          lastActivity: serverTimestamp()
        });
      } else {
        await setDoc(ipRef, {
          ip,
          users: [],
          anonymousVisits: 1,
          firstSeen: serverTimestamp(),
          lastActivity: serverTimestamp(),
          banned: false,
          limited: false,
          type: this.isIPv6(ip) ? 'ipv6' : 'ipv4',
          ...(this.isIPv6(ip) && { networkPrefix: effectiveIP })
        });
      }

      // For IPv6, also log network activity
      if (this.isIPv6(ip) && effectiveIP !== ip) {
        const networkRef = doc(db, 'ip_addresses', effectiveIP);
        const networkDoc = await getDoc(networkRef);

        if (networkDoc.exists()) {
          await updateDoc(networkRef, {
            anonymousVisits: (networkDoc.data().anonymousVisits || 0) + 1,
            lastActivity: serverTimestamp(),
            lastSeenIP: ip
          });
        } else {
          await setDoc(networkRef, {
            ip: effectiveIP,
            users: [],
            anonymousVisits: 1,
            firstSeen: serverTimestamp(),
            lastActivity: serverTimestamp(),
            banned: false,
            limited: false,
            type: 'ipv6_network',
            lastSeenIP: ip,
            isNetworkPrefix: true
          });
        }
      }

      return true;
    } catch (error) {
      console.error('Error logging anonymous IP:', error);
      return false;
    }
  }
}

export default new IPService(); 